import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";

/**
 * The site's primary button, including the sheen that sweeps across on hover.
 *
 * This single component replaces four hand-copied ~90-line `sx` blocks that had
 * already drifted apart from each other.
 *
 * @prop {"solid"|"subtle"} tone
 */
const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "tone",
})(({ theme, tone = "solid" }) => {
  const { invert, sheen, hairline } = theme.tokens;
  const solid = tone === "solid";

  return {
    padding: "8px 20px",
    borderRadius: 8,
    fontSize: "0.8125rem",
    fontWeight: 500,
    position: "relative",
    overflow: "hidden",
    border: hairline,
    transition: theme.transitions.create(
      ["background-color", "color", "box-shadow", "transform"],
      { duration: 200 }
    ),
    backgroundColor: solid ? invert.background : "transparent",
    color: solid ? invert.color : theme.palette.text.primary,

    "& .MuiButton-endIcon, & .MuiButton-startIcon": {
      transition: "transform 0.2s ease",
    },

    "&:hover": {
      backgroundColor: solid ? invert.hoverBackground : invert.hoverBackground,
      color: invert.hoverColor,
      boxShadow: invert.shadow,
      "& .MuiButton-endIcon": { transform: "translateX(3px)" },
    },

    "&:active": { transform: "translateY(1px)" },

    // Sheen sweep. Must be exactly one button-width, parked fully off to the
    // left. `inset: 0` here would stretch it to 200% and leave the bright
    // middle of the gradient sitting on the button at rest.
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "100%",
      height: "100%",
      background: sheen,
      transition: "left 0.5s ease",
      pointerEvents: "none",
    },
    "&:hover::before": { left: "100%" },
    "@media (prefers-reduced-motion: reduce)": {
      "&::before": { display: "none" },
    },
  };
});

export default ActionButton;
